import type { Vendor, VendorCategoryInfo } from '../types/vendors';
import { Camera, Music, Utensils, Flower, Palette, Car, Gift, User, Video, Sparkles } from 'lucide-react';

export const vendorCategories: VendorCategoryInfo[] = [
  { id: 'all', name: 'All Vendors', icon: Gift, color: 'bg-gray-100 text-gray-800', description: 'Browse all wedding vendors' },
  { id: 'photographers', name: 'Photographers', icon: Camera, color: 'bg-pink-100 text-pink-800', description: 'Capture your special moments' },
  { id: 'videographers', name: 'Videographers', icon: Video, color: 'bg-red-100 text-red-800', description: 'Professional wedding videography' },
  { id: 'djs', name: 'DJs & Music', icon: Music, color: 'bg-blue-100 text-blue-800', description: 'Music and entertainment' },
  { id: 'caterers', name: 'Caterers', icon: Utensils, color: 'bg-green-100 text-green-800', description: 'Delicious wedding catering' },
  { id: 'florists', name: 'Florists', icon: Flower, color: 'bg-purple-100 text-purple-800', description: 'Beautiful floral arrangements' },
  { id: 'decorators', name: 'Decorators', icon: Palette, color: 'bg-yellow-100 text-yellow-800', description: 'Wedding decoration and styling' },
  { id: 'transport', name: 'Transport', icon: Car, color: 'bg-indigo-100 text-indigo-800', description: 'Wedding transportation services' },
  { id: 'makeup', name: 'Makeup Artists', icon: Sparkles, color: 'bg-rose-100 text-rose-800', description: 'Bridal makeup and styling' },
  { id: 'planners', name: 'Wedding Planners', icon: User, color: 'bg-teal-100 text-teal-800', description: 'Professional wedding coordination' }
];

export const sampleVendors: Vendor[] = [
  {
    id: '1',
    name: 'Capture Moments Photography',
    category: 'photographers',
    location: 'Colombo 07, Western Province',
    city: 'Colombo',
    priceRange: { min: 75000, max: 150000, unit: 'per event' },
    rating: 4.9,
    reviews: 42,
    image: 'https://images.unsplash.com/photo-1606216794074-735e91aa2c92?w=800&h=600&fit=crop',
    specialties: ['Wedding Photography', 'Engagement Shoots', 'Pre-wedding Sessions', 'Candid Photography'],
    description: 'Award-winning wedding photographers specializing in candid moments and artistic compositions. Over 8 years of experience capturing love stories.',
    contact: {
      phone: '+94 77 123 4567',
      email: 'info@capturemoments.lk',
      website: 'www.capturemoments.lk'
    },
    portfolio: [
      'https://images.unsplash.com/photo-1511285560929-80b456fea0bc',
      'https://images.unsplash.com/photo-1465495976277-4387d4b0e4a6'
    ],
    verified: true
  },
  {
    id: '2',
    name: 'Melody Makers DJ Service',
    category: 'djs',
    location: 'Kandy, Central Province',
    city: 'Kandy',
    priceRange: { min: 25000, max: 50000, unit: 'per event' },
    rating: 4.7,
    reviews: 28,
    image: 'https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=800&h=600&fit=crop',
    specialties: ['Wedding Reception', 'Live Music', 'Sound System', 'MC Services'],
    description: 'Professional DJ service with extensive music library and high-quality sound equipment. Experienced in reading the crowd and keeping the party alive.',
    contact: {
      phone: '+94 81 234 5678',
      email: 'bookings@melodymakers.lk',
      website: 'www.melodymakers.lk'
    },
    verified: true
  },
  {
    id: '3',
    name: 'Gourmet Catering Co.',
    category: 'caterers',
    location: 'Galle, Southern Province',
    city: 'Galle',
    priceRange: { min: 2500, max: 5000, unit: 'per person' },
    rating: 4.8,
    reviews: 35,
    image: 'https://images.unsplash.com/photo-1555244162-803834f70033?w=800&h=600&fit=crop',
    specialties: ['Sri Lankan Cuisine', 'International Menu', 'Buffet Service', 'Live Cooking Stations'],
    description: 'Premium catering service offering authentic Sri Lankan cuisine and international dishes. Farm-to-table ingredients and professional presentation.',
    contact: {
      phone: '+94 91 345 6789',
      email: 'catering@gourmetco.lk',
      website: 'www.gourmetcatering.lk'
    },
    verified: true
  },
  {
    id: '4',
    name: 'Bloom & Blossom Florists',
    category: 'florists',
    location: 'Negombo, Western Province',
    city: 'Negombo',
    priceRange: { min: 30000, max: 80000, unit: 'per event' },
    rating: 4.6,
    reviews: 19,
    image: 'https://images.unsplash.com/photo-1578662996442-48f60103fc96?w=800&h=600&fit=crop',
    specialties: ['Bridal Bouquets', 'Centerpieces', 'Ceremony Decor', 'Floral Arches'],
    description: 'Creative floral designers specializing in romantic and elegant wedding arrangements using fresh, locally-sourced flowers.',
    contact: {
      phone: '+94 31 456 7890',
      email: 'flowers@bloomblossom.lk',
      website: 'www.bloomblossom.lk'
    },
    verified: true
  },
  {
    id: '5',
    name: 'Elegant Events Decoration',
    category: 'decorators',
    location: 'Colombo 05, Western Province',
    city: 'Colombo',
    priceRange: { min: 100000, max: 300000, unit: 'per event' },
    rating: 4.8,
    reviews: 26,
    image: 'https://images.unsplash.com/photo-1464366400600-7168b8af9bc3?w=800&h=600&fit=crop',
    specialties: ['Theme Decoration', 'Lighting Design', 'Stage Setup', 'Backdrop Creation'],
    description: 'Full-service wedding decoration company creating magical atmospheres with custom themes, professional lighting, and stunning backdrops.',
    contact: {
      phone: '+94 11 567 8901',
      email: 'info@elegantevents.lk',
      website: 'www.elegantevents.lk'
    },
    verified: true
  },
  {
    id: '6',
    name: 'Royal Wedding Cars',
    category: 'transport',
    location: 'Colombo 03, Western Province',
    city: 'Colombo',
    priceRange: { min: 15000, max: 45000, unit: 'per day' },
    rating: 4.5,
    reviews: 33,
    image: 'https://images.unsplash.com/photo-1449824913935-59a10b8d2000?w=800&h=600&fit=crop',
    specialties: ['Luxury Cars', 'Vintage Vehicles', 'Decorated Cars', 'Chauffeur Service'],
    description: 'Premium wedding transportation service featuring luxury and vintage vehicles with professional chauffeurs and custom decorations.',
    contact: {
      phone: '+94 11 678 9012',
      email: 'bookings@royalweddingcars.lk',
      website: 'www.royalweddingcars.lk'
    },
    verified: true
  },
  {
    id: '7',
    name: 'Glamour Bridal Makeup',
    category: 'makeup',
    location: 'Colombo 07, Western Province',
    city: 'Colombo',
    priceRange: { min: 25000, max: 60000, unit: 'per session' },
    rating: 4.9,
    reviews: 51,
    image: 'https://images.unsplash.com/photo-1487412947147-5cebf100ffc2?w=800&h=600&fit=crop',
    specialties: ['Bridal Makeup', 'Hair Styling', 'Trial Sessions', 'Airbrush Makeup'],
    description: 'Professional bridal makeup artists with expertise in traditional and contemporary styles. Includes trial sessions and touch-up services.',
    contact: {
      phone: '+94 77 789 0123',
      email: 'bookings@glamourbridal.lk',
      website: 'www.glamourbridal.lk'
    },
    verified: true
  },
  {
    id: '8',
    name: 'Dream Weddings Planner',
    category: 'planners',
    location: 'Kandy, Central Province',
    city: 'Kandy',
    priceRange: { min: 150000, max: 500000, unit: 'per event' },
    rating: 4.8,
    reviews: 14,
    image: 'https://images.unsplash.com/photo-1511578314322-379afb476865?w=800&h=600&fit=crop',
    specialties: ['Full Wedding Planning', 'Day-of Coordination', 'Vendor Management', 'Timeline Creation'],
    description: 'Experienced wedding planners offering comprehensive planning services from concept to execution. Stress-free wedding coordination.',
    contact: {
      phone: '+94 81 890 1234',
      email: 'hello@dreamweddings.lk',
      website: 'www.dreamweddings.lk'
    },
    verified: true
  }
];

export const vendorLocations = [
  'All Locations',
  'Colombo',
  'Kandy',
  'Galle',
  'Negombo',
  'Nuwara Eliya',
  'Bentota',
  'Anuradhapura'
];